import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredChangeRequests,
  selectChangeRequestFilterVisible,
  selectChangeRequestLoading,
  selectFetchChangeRequestLoading,
} from './store/selectors';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { isHeaderVisible } from 'Core/redux/main/selectors';
import { Edit, Trash2 } from 'react-feather';
import { Button, ButtonGroup } from 'react-bootstrap';
import { deleteChangeRequest, fetchChangeRequestById } from './store/slice';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import VirtualList from '../components/VirtualList';

const ChangeRequestTile = () => {
  const [columnCount, setColumnCount] = useState(3);
  const [columnWidth, setColumnWidth] = useState(300);
  const tileRef = useRef(null);

  const filteredData = useSelector(selectFilteredChangeRequests);
  const isLoading = useSelector(selectChangeRequestLoading);
  const filterVisible = useSelector(selectChangeRequestFilterVisible);
  const isFetchDataLoading = useSelector(selectFetchChangeRequestLoading);
  const headerVisible = useSelector(isHeaderVisible);

  const updateColumnSettings = useCallback(() => {
    if (tileRef.current) {
      const width = tileRef.current.offsetWidth;
      let columns = 3;
      if (width < 576) columns = 1;
      else if (width < 768) columns = 2;
      else if (width < 1200) columns = 3;
      else columns = 4;
      setColumnCount(columns);
      setColumnWidth(Math.max(width / columns, 250));
    }
  }, []);

  useEffect(() => {
    updateColumnSettings();
    window.addEventListener('resize', updateColumnSettings);
    return () => window.removeEventListener('resize', updateColumnSettings);
  }, [updateColumnSettings]);

  const itemData = (filteredData || []).map(cr => <CRTile cr={cr} />);

  return (
    <div
      className={`tile-body ${filterVisible ? 'filter-visible' : ''} ${
        !headerVisible ? 'header-collapsed' : ''
      }`}
      ref={tileRef}
    >
      {(isLoading || isFetchDataLoading) && <LoadingSpinner />}
      <VirtualList
        columnCount={columnCount}
        rowCount={Math.ceil((filteredData || []).length / columnCount)}
        itemData={itemData}
        columnWidth={columnWidth}
        rowHeight={180}
      />
    </div>
  );
};

const CRTile = ({ cr }) => {
  const dispatch = useDispatch();

  const handleEdit = e => {
    e.stopPropagation();
    dispatch(fetchChangeRequestById(cr.id));
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
    if (result.isConfirmed) {
      dispatch(deleteChangeRequest(cr.id));
    }
  };

  const approvalsColor =
    cr.approvalsCount === cr.approvalsTotal
      ? '#28a745'
      : cr.approvalsCount >= cr.approvalsTotal / 2
      ? '#ffc107'
      : '#dc3545';

  return (
    <div key={cr.id} className="tile" style={{ padding: '4px' }}>
      <div
        className={`card p-3 m-1 h-100 ${cr.isNewlyCreated ? 'border-success' : ''}`}
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          position: 'relative',
        }}
      >
        {/* Actions */}
        <div className="tile-actions">
          <ButtonGroup size="sm">
            <Button variant="light" onClick={handleEdit} title="Edit">
              <Edit size={15} />
            </Button>
            <Button variant="light" onClick={handleDelete} title="Delete">
              <Trash2 size={15} />
            </Button>
          </ButtonGroup>
        </div>

        {/* CR Number */}
        <div className="mb-1">
          <span style={{ fontWeight: '700', color: '#1a56db', fontSize: '0.85rem' }}>
            {cr.crNumber}
          </span>
        </div>

        {/* Title */}
        <h6 className="mb-2 fw-bold" style={{ fontSize: '0.95rem', lineHeight: '1.3' }}>
          {cr.title}
        </h6>

        {/* Status badge */}
        <div className="mb-2">
          <span
            className="badge"
            style={{
              backgroundColor: cr.backgroundColor || '#e0e0e0',
              color: cr.textColor || '#000',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '0.78rem',
            }}
          >
            {cr.status}
          </span>
        </div>

        {/* Initiator */}
        <div className="mb-2" style={{ fontSize: '0.82rem', color: '#555' }}>
          <span style={{ fontWeight: '600' }}>Initiator: </span>
          {cr.initiator}
        </div>

        {/* Approvals */}
        <div>
          <span
            className="badge"
            style={{
              backgroundColor: approvalsColor,
              color: '#fff',
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '0.78rem',
            }}
          >
            {cr.approvalsCount}/{cr.approvalsTotal} Approved
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChangeRequestTile;
